'use client'

import { useState, useEffect, useMemo } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'

interface Workshop {
  id: string
  slug: string
  title: string
  description: string | null
  date: string
  duration: number
  meetingLink: string | null
  isActive: boolean
}

export default function CRPOWorkshopPage() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const source = searchParams.get('source')
  const [workshop, setWorkshop] = useState<Workshop | null>(null)
  const [workshopLoading, setWorkshopLoading] = useState(true)
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, mins: 0, secs: 0 })
  const [openFaq, setOpenFaq] = useState(0)
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    school: '',
    studentStatus: '',
    registeredWithCRPO: '',
    referralSource: '',
    referredByName: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')

  // Fetch active workshop
  useEffect(() => {
    async function fetchWorkshop() {
      try {
        const res = await fetch('/api/workshops/crpo')
        if (res.ok) {
          const data = await res.json()
          setWorkshop(data)
        }
      } catch (err) {
        console.error('Failed to fetch workshop:', err)
      } finally {
        setWorkshopLoading(false)
      }
    }
    fetchWorkshop()
  }, [])

  // Workshop date for countdown
  const workshopDate = useMemo(() => workshop ? new Date(workshop.date) : null, [workshop])

  // Formatted dates
  const formattedShortDate = useMemo(() => {
    if (!workshopDate) return ''
    return workshopDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      timeZone: 'America/New_York'
    })
  }, [workshopDate])

  const formattedDateWithYear = useMemo(() => {
    if (!workshopDate) return ''
    return workshopDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'America/New_York'
    })
  }, [workshopDate])

  const formattedTime = useMemo(() => {
    if (!workshopDate) return ''
    const time = workshopDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'America/New_York'
    })
    return `${time} ET`
  }, [workshopDate])

  // Countdown timer
  useEffect(() => {
    if (!workshopDate) return

    const updateCountdown = () => {
      const now = new Date()
      const diff = workshopDate.getTime() - now.getTime()

      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, mins: 0, secs: 0 })
        return
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24))
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
      const secs = Math.floor((diff % (1000 * 60)) / 1000)

      setCountdown({ days, hours, mins, secs })
    }

    updateCountdown()
    const interval = setInterval(updateCountdown, 1000)
    return () => clearInterval(interval)
  }, [workshopDate])

  const scrollToRegister = () => {
    document.getElementById('register')?.scrollIntoView({ behavior: 'smooth' })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!workshop) return

    setIsSubmitting(true)
    setError('')

    try {
      const response = await fetch('/api/workshops/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workshopId: workshop.id,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          school: formData.school,
          studentStatus: formData.studentStatus,
          registeredWithCRPO: formData.registeredWithCRPO === 'yes',
          referralSource: formData.referralSource || null,
          referredByName: formData.referralSource === 'FRIEND' ? formData.referredByName : null,
          source: source || null,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        setError(data.error || 'Something went wrong. Please try again.')
        setIsSubmitting(false)
        return
      }

      router.push(`/workshops/crpo/thank-you?workshop=${workshop.slug}`)
    } catch {
      setError('Something went wrong. Please try again.')
      setIsSubmitting(false)
    }
  }

  const faqs = [
    {
      question: "I'm feeling overwhelmed by all the CRPO requirements — will this help?",
      answer: "Yes, that's exactly why we built this. The CRPO website is confusing, the requirements are scattered, and nobody explains the actual timeline. This workshop gives you a clear checklist and helps you map everything to your target exam date — so you can stop guessing and start planning."
    },
    {
      question: "How is this different from just reading the CRPO website?",
      answer: "The CRPO website tells you what's required. This workshop shows you how to actually get there — with a clear timeline, common mistakes to avoid, and a calculator that tracks your progress. Plus, you'll hear from people who've been through it and can answer your real questions."
    },
    {
      question: 'Is this really free?',
      answer: "Yes, 100% free. No credit card, no hidden fees, no catch. We built this because we know how confusing the post-placement process can be, and we want to help."
    },
    {
      question: "I'm not from Yorkville — can I still attend?",
      answer: "Absolutely. This workshop is for any graduating intern preparing for the CRPO exam, regardless of where you did your placement or which school you attended."
    },
    {
      question: "What if I can't attend live?",
      answer: "Register anyway — you'll get the recording. But we'd love to have you live. The Q&A is the best part, and you can get your specific questions answered. Note: PD credits are only available for live attendees."
    },
    {
      question: "What's the CRPO Progress Calculator?",
      answer: "A free tool that shows you exactly where you stand. Input your client hours, supervision hours, and PD hours — and see what's done, what's left, and your timeline to your target exam date. You'll get instant access when you register."
    },
    {
      question: 'How does the "Refer a Friend" bonus work?',
      answer: "Share this page with a classmate. When they sign up and enter your name in the \"Who referred you?\" field, you'll both receive an exclusive CRPO Prep Resource Pack after the workshop."
    },
    {
      question: "Does this count toward my PD hours?",
      answer: "Yes. You'll receive attendance documentation after the workshop. It's one of the 40 hours you need over 2 years — and this one's free."
    },
  ]

  if (workshopLoading) {
    return (
      <div className="min-h-screen bg-[#FDF8F0] font-sans -mt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#D4E4D4] border-t-[#5E7D5E] rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#7A7A7A]">Loading workshop...</p>
        </div>
      </div>
    )
  }

  if (!workshop) {
    return (
      <div className="min-h-screen bg-[#FDF8F0] font-sans -mt-20 flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#7A7A7A] mb-4">No active workshop found.</p>
          <a href="/workshops" className="text-[#5E7D5E] font-medium hover:underline">
            ← Back to workshops
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#FDF8F0] font-sans -mt-20">
      {/* Hero */}
      <div className="relative pt-32 pb-24 px-6 text-center overflow-hidden">
        <div className="absolute -top-[200px] -right-[200px] w-[600px] h-[600px] bg-[radial-gradient(circle,rgba(139,170,139,0.12)_0%,transparent_70%)] rounded-full" />
        <div className="absolute -bottom-[100px] -left-[150px] w-[400px] h-[400px] bg-[radial-gradient(circle,rgba(193,123,74,0.08)_0%,transparent_70%)] rounded-full" />

        <div className="max-w-[720px] mx-auto relative z-10">
          {/* Combined top badge: Free + Date/Time */}
          <div className="inline-flex items-center gap-3 bg-white/80 backdrop-blur-sm rounded-full pl-1.5 pr-5 py-1.5 shadow-[0_2px_15px_rgba(0,0,0,0.06)] border border-[#8BAA8B]/20 mb-8 animate-fadeInDown">
            <span className="bg-gradient-to-r from-[#5E7D5E] to-[#4A6B4A] text-white px-3.5 py-1.5 rounded-full text-sm font-bold">
              Free Virtual Workshop
            </span>
            <span className="text-[#4A4A4A] text-sm font-medium">{formattedShortDate} · {formattedTime}</span>
          </div>

          <h1 className="font-serif text-4xl md:text-5xl lg:text-[3.4rem] text-[#2D2D2D] leading-[1.1] mb-8 animate-fadeInUp">
            Placement Is Done.<br />
            <em className="not-italic text-[#C17B4A] relative">
              Now What?
              <span className="absolute bottom-0.5 left-0 right-0 h-[3px] bg-[#C17B4A]/30 rounded" />
            </em>
          </h1>

          <p className="text-lg text-[#7A7A7A] mb-8 max-w-[480px] mx-auto animate-fadeInUp animation-delay-100 leading-relaxed">
            Map your CRPO exam timeline. Avoid costly mistakes. Get a clear path to your target exam date.
          </p>

          <button
            onClick={scrollToRegister}
            className="inline-flex items-center gap-2.5 bg-[#C17B4A] hover:bg-[#a86a3f] text-white px-8 py-4 rounded-full font-bold text-lg transition-all hover:-translate-y-0.5 shadow-[0_4px_20px_rgba(193,123,74,0.3)] hover:shadow-[0_6px_28px_rgba(193,123,74,0.4)] animate-fadeInUp animation-delay-200"
          >
            Register Now
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Countdown */}
      <div className="px-6 pb-24 text-center">
        <p className="text-sm text-[#7A7A7A] mb-5">
          <span className="text-base mr-1">📜</span>
          Counts toward your professional development hours
        </p>
        <div className="max-w-[600px] mx-auto bg-white rounded-2xl px-9 py-7 shadow-[0_2px_20px_rgba(0,0,0,0.06)] border border-[#8BAA8B]/15">
          <div className="text-sm font-semibold text-[#8BAA8B] uppercase tracking-wider mb-4">
            Workshop starts in
          </div>
          <div className="flex justify-center gap-6">
            {[
              { value: countdown.days, label: 'Days' },
              { value: countdown.hours, label: 'Hours', sep: true },
              { value: countdown.mins, label: 'Min', sep: true },
              { value: countdown.secs, label: 'Sec', sep: true }
            ].map((item, i) => (
              <div key={item.label} className="flex items-center gap-6">
                {item.sep && <span className="font-serif text-4xl text-[#D4E4D4]">:</span>}
                <div className="flex flex-col items-center">
                  <span className="font-serif text-4xl text-[#2D2D2D]">
                    {String(item.value).padStart(2, '0')}
                  </span>
                  <span className="text-xs text-[#7A7A7A] uppercase tracking-wide mt-1">
                    {item.label}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* This Is For You If */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-[1100px] mx-auto">
          <div className="text-xs font-bold text-[#8BAA8B] uppercase tracking-widest mb-3">Is this for me?</div>
          <h2 className="font-serif text-3xl md:text-4xl text-[#2D2D2D] mb-4">This workshop is for you if...</h2>
          <p className="text-lg text-[#7A7A7A] max-w-[600px] mb-12">
            No matter where you did your placement, if any of these sound familiar, this was made for you.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: '🤔', text: 'You\'ve finished placement and are thinking <strong>"okay… now what?"</strong>' },
              { icon: '🗺️', text: 'You want a <strong>clear plan</strong> instead of vague studying and guesswork' },
              { icon: '🎯', text: 'You want to <strong>hit your target exam date</strong> this year' },
              { icon: '⚠️', text: 'You don\'t want to <strong>miss important steps or deadlines</strong>' },
              { icon: '💰', text: 'You want to avoid <strong>last-minute panic</strong> and wasted study time' },
              { icon: '🧠', text: 'You want to study in a way that builds <strong>judgment, not just memory</strong>' }
            ].map((item) => (
              <div key={item.icon} className="bg-[#FDF8F0] rounded-2xl p-7 border border-[#8BAA8B]/10 hover:border-[#D4E4D4] hover:-translate-y-0.5 hover:shadow-[0_2px_20px_rgba(0,0,0,0.06)] transition-all flex gap-4">
                <div className="w-[42px] h-[42px] min-w-[42px] bg-[#EDF5ED] rounded-xl flex items-center justify-center text-xl">
                  {item.icon}
                </div>
                <p className="text-[#4A4A4A] leading-relaxed" dangerouslySetInnerHTML={{ __html: item.text }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We'll Cover */}
      <section className="py-24 px-6">
        <div className="max-w-[1100px] mx-auto">
          <div className="text-xs font-bold text-[#8BAA8B] uppercase tracking-widest mb-3">What you'll learn</div>
          <h2 className="font-serif text-3xl md:text-4xl text-[#2D2D2D] mb-4">What We'll Cover</h2>
          <p className="text-lg text-[#7A7A7A] max-w-[600px] mb-12">
            {workshop?.duration || 80} minutes of real, practical guidance — not vague motivational fluff.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { num: '01', title: 'The CRPO Exam: Purpose & Structure', desc: "What the exam actually tests, how it's structured, and what the CRPO is really looking for." },
              { num: '02', title: 'The Full Prep Checklist', desc: 'Every step from placement to exam day — so nothing gets missed.' },
              { num: '03', title: 'Map Your Timeline', desc: 'How to map your prep to your target exam date.' },
              { num: '04', title: 'Common Mistakes', desc: 'The mistakes that cost people time, money, and confidence.' },
              { num: '05', title: 'Study for Judgment', desc: 'How to build clinical judgment — not just memorization.' },
              { num: '06', title: 'Connect With Peers', desc: 'Meet other graduating interns in small breakout groups.' }
            ].map((item) => (
              <div key={item.num} className="bg-white rounded-2xl p-8 shadow-[0_2px_20px_rgba(0,0,0,0.06)] border-l-4 border-[#8BAA8B] hover:-translate-y-0.5 hover:shadow-[0_4px_30px_rgba(0,0,0,0.08)] transition-all">
                <div className="font-serif text-3xl text-[#D4E4D4] mb-2">{item.num}</div>
                <div className="font-bold text-[#2D2D2D] text-lg mb-2">{item.title}</div>
                <p className="text-sm text-[#7A7A7A] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Promise */}
      <section className="py-24 px-6 bg-[#5E7D5E] text-center">
        <div className="max-w-[700px] mx-auto">
          <div className="text-xs font-bold text-[#D4E4D4] uppercase tracking-widest mb-3">Our promise</div>
          <p className="font-serif text-2xl md:text-3xl text-white leading-snug mb-2">
            When you leave, you'll have a clear timeline mapped to your target exam date — and the confidence that you're set up for success.
          </p>
          <p className="text-[#D4E4D4]/80">— That's the whole point of this workshop.</p>
        </div>
      </section>

      {/* Calculator Preview */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-[1100px] mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-xs font-bold text-[#8BAA8B] uppercase tracking-widest mb-3">Free tool</div>
              <h3 className="font-serif text-2xl md:text-3xl text-[#2D2D2D] mb-4 leading-snug">
                CRPO Progress Calculator
              </h3>
              <p className="text-[#7A7A7A] mb-5">
                Enter your current hours and get a personalized report showing exactly where you stand — with a clear breakdown of what's left to complete and a timeline to your target exam date.
              </p>
              <ul className="space-y-3">
                {[
                  'Choose your path: exam or independent practice',
                  'Input your client hours and supervision hours',
                  'Set your target exam date',
                  'Get a personalized progress report emailed to you',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-[#4A4A4A]">
                    <span className="w-[22px] h-[22px] min-w-[22px] bg-[#EDF5ED] rounded-full flex items-center justify-center text-[#5E7D5E] text-xs font-bold mt-0.5">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={scrollToRegister}
                className="inline-flex items-center gap-2 mt-6 bg-[#5E7D5E] hover:bg-[#4A6B4A] text-white px-6 py-3 rounded-full font-semibold text-sm transition-colors"
              >
                Register to Get the Calculator
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            <div className="bg-[#FDF8F0] rounded-2xl p-8 border border-[#8BAA8B]/15">
              {/* Mock calculator preview */}
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 bg-[#5E7D5E] rounded-lg flex items-center justify-center">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <div className="font-bold text-[#2D2D2D]">Your Progress Report</div>
              </div>

              {/* Progress bars preview */}
              <div className="space-y-5">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="font-medium text-[#2D2D2D]">Client Hours</span>
                    <span className="text-[#5E7D5E] font-semibold">225 / 450</span>
                  </div>
                  <div className="h-3 bg-[#E5E5E5] rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#5E7D5E] to-[#8BAA8B] rounded-full" style={{ width: '50%' }} />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="font-medium text-[#2D2D2D]">Individual Supervision</span>
                    <span className="text-[#5E7D5E] font-semibold">38 / 50</span>
                  </div>
                  <div className="h-3 bg-[#E5E5E5] rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#5E7D5E] to-[#8BAA8B] rounded-full" style={{ width: '76%' }} />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="font-medium text-[#2D2D2D]">Group Supervision</span>
                    <span className="text-[#5E7D5E] font-semibold">12 / 50</span>
                  </div>
                  <div className="h-3 bg-[#E5E5E5] rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#5E7D5E] to-[#8BAA8B] rounded-full" style={{ width: '24%' }} />
                  </div>
                </div>
              </div>

              {/* Summary card */}
              <div className="mt-6 p-4 bg-white rounded-xl border border-[#D4E4D4]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#FFF3CD] rounded-full flex items-center justify-center text-lg">
                    ⏳
                  </div>
                  <div>
                    <div className="font-semibold text-[#2D2D2D] text-sm">225 more client hours needed</div>
                    <div className="text-xs text-[#7A7A7A]">On track for April 2026 exam</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-6 bg-[#EDF5ED]">
        <div className="max-w-[1100px] mx-auto">
          <div className="text-xs font-bold text-[#8BAA8B] uppercase tracking-widest mb-3">From the community</div>
          <h2 className="font-serif text-3xl md:text-4xl text-[#2D2D2D] mb-4">What Others Are Saying</h2>
          <p className="text-lg text-[#7A7A7A] max-w-[600px] mb-12">
            Real words from people who've been exactly where you are.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { initials: 'AK', name: 'A.K.', role: 'Yorkville Graduate, 2025', text: "I wish this existed when I was finishing placement. I spent weeks trying to figure out the CRPO website. Having a clear checklist would have saved me so much anxiety." },
              { initials: 'MS', name: 'M.S.', role: 'Between Sessions Member', text: "The community aspect surprised me. After placement, you lose that built-in support. Finding others going through the same thing made such a difference." },
              { initials: 'JL', name: 'J.L.', role: 'Laurier Graduate, 2025', text: "I was panic-studying and second-guessing everything. Once I understood how the exam was structured, my whole approach changed. I felt so much calmer." }
            ].map((item) => (
              <div key={item.initials} className="bg-white rounded-2xl p-8 shadow-[0_2px_20px_rgba(0,0,0,0.06)]">
                <div className="font-serif text-5xl text-[#D4E4D4] leading-none mb-3">"</div>
                <p className="text-[#4A4A4A] italic leading-relaxed mb-5">{item.text}</p>
                <div className="flex items-center gap-3">
                  <div className="w-[42px] h-[42px] bg-[#F5E6D8] rounded-full flex items-center justify-center font-bold text-[#C17B4A] text-sm">
                    {item.initials}
                  </div>
                  <div>
                    <div className="font-semibold text-[#2D2D2D] text-sm">{item.name}</div>
                    <div className="text-xs text-[#7A7A7A]">{item.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section id="register" className="py-24 px-6 bg-white">
        <div className="max-w-[1100px] mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <div>
              <div className="text-xs font-bold text-[#8BAA8B] uppercase tracking-widest mb-3">Free workshop</div>
              <h2 className="font-serif text-3xl md:text-4xl text-[#2D2D2D] mb-4">Register for {formattedShortDate}</h2>
              <p className="text-[#7A7A7A] mb-6">
                {formattedDateWithYear} at {formattedTime} via Google Meet. You'll get instant access to the CRPO Progress Calculator when you sign up.
              </p>

              <ul className="space-y-3">
                {[
                  { icon: '✓', text: '<strong>Instant access</strong> to the CRPO Progress Calculator' },
                  { icon: '✓', text: `<strong>${workshop?.duration || 80}-minute live session</strong> with Q&A and breakouts` },
                  { icon: '📜', text: '<strong>Counts toward your PD hours</strong> (certificate provided)' },
                  { icon: '✓', text: "<strong>Can't make it live?</strong> Recording sent after" },
                  { icon: '🎁', text: '<strong>Refer a friend</strong> — you both get a bonus resource pack' }
                ].map((item) => (
                  <li key={item.text} className="flex items-start gap-3 text-sm text-[#4A4A4A]">
                    <div className="w-7 h-7 min-w-7 bg-[#F5E6D8] rounded-lg flex items-center justify-center text-xs">
                      {item.icon}
                    </div>
                    <span dangerouslySetInnerHTML={{ __html: item.text }} />
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-[#FDF8F0] rounded-2xl p-9 border-2 border-[#D4E4D4]">
              <div className="font-serif text-xl text-[#2D2D2D] mb-1">You're Almost In</div>
              <p className="text-sm text-[#7A7A7A] mb-6">Join 150+ interns who already have a plan.</p>

              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-[#2D2D2D] mb-1.5">First Name</label>
                    <input
                      type="text"
                      required
                      placeholder="First name"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      className="w-full px-4 py-3 border-[1.5px] border-gray-200 rounded-lg bg-white text-[#2D2D2D] focus:border-[#8BAA8B] focus:ring-2 focus:ring-[#8BAA8B]/15 outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#2D2D2D] mb-1.5">Last Name</label>
                    <input
                      type="text"
                      required
                      placeholder="Last name"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      className="w-full px-4 py-3 border-[1.5px] border-gray-200 rounded-lg bg-white text-[#2D2D2D] focus:border-[#8BAA8B] focus:ring-2 focus:ring-[#8BAA8B]/15 outline-none transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#2D2D2D] mb-1.5">Email Address</label>
                  <input
                    type="email"
                    required
                    placeholder="you@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 border-[1.5px] border-gray-200 rounded-lg bg-white text-[#2D2D2D] focus:border-[#8BAA8B] focus:ring-2 focus:ring-[#8BAA8B]/15 outline-none transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#2D2D2D] mb-1.5">School / Program</label>
                  <select
                    required
                    value={formData.school}
                    onChange={(e) => setFormData({ ...formData, school: e.target.value })}
                    className="w-full px-4 py-3 border-[1.5px] border-gray-200 rounded-lg bg-white text-[#2D2D2D] focus:border-[#8BAA8B] focus:ring-2 focus:ring-[#8BAA8B]/15 outline-none transition-all"
                  >
                    <option value="" disabled>Select your school</option>
                    <option>Yorkville University</option>
                    <option>Wilfrid Laurier University</option>
                    <option>City University of Seattle</option>
                    <option>Tyndale University</option>
                    <option>Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#2D2D2D] mb-1.5">Where are you in your program?</label>
                  <div className="space-y-2.5 mt-2">
                    {[
                      { value: 'coursework', label: 'In coursework (pre-placement)' },
                      { value: 'placement', label: 'Currently in placement' },
                      { value: 'completed', label: 'Completed placement' }
                    ].map((option) => (
                      <label key={option.value} className="flex items-center gap-2.5 cursor-pointer text-sm text-[#2D2D2D]">
                        <input
                          type="radio"
                          name="studentStatus"
                          value={option.value}
                          checked={formData.studentStatus === option.value}
                          onChange={(e) => setFormData({ ...formData, studentStatus: e.target.value })}
                          className="w-4 h-4 accent-[#8BAA8B]"
                        />
                        {option.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#2D2D2D] mb-1.5">Are you registered with CRPO?</label>
                  <div className="space-y-2.5 mt-2">
                    {[
                      { value: 'yes', label: "Yes, I'm RP (Qualifying)" },
                      { value: 'no', label: 'Not yet' }
                    ].map((option) => (
                      <label key={option.value} className="flex items-center gap-2.5 cursor-pointer text-sm text-[#2D2D2D]">
                        <input
                          type="radio"
                          name="registeredWithCRPO"
                          value={option.value}
                          checked={formData.registeredWithCRPO === option.value}
                          onChange={(e) => setFormData({ ...formData, registeredWithCRPO: e.target.value })}
                          className="w-4 h-4 accent-[#8BAA8B]"
                        />
                        {option.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#2D2D2D] mb-1.5">
                    How did you hear about this? <span className="font-normal text-[#7A7A7A]">(optional)</span>
                  </label>
                  <select
                    value={formData.referralSource}
                    onChange={(e) => setFormData({ ...formData, referralSource: e.target.value, referredByName: '' })}
                    className="w-full px-4 py-3 border-[1.5px] border-gray-200 rounded-lg bg-white text-[#2D2D2D] focus:border-[#8BAA8B] focus:ring-2 focus:ring-[#8BAA8B]/15 outline-none transition-all"
                  >
                    <option value="">Select an option</option>
                    <option value="FRIEND">A friend or classmate</option>
                    <option value="SCHOOL">School/program announcement</option>
                    <option value="SOCIAL_MEDIA">Social media</option>
                    <option value="ALLI_NEWSLETTER">Alli newsletter</option>
                    <option value="GOOGLE_SEARCH">Google search</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>

                {formData.referralSource === 'FRIEND' && (
                  <div>
                    <label className="block text-sm font-semibold text-[#2D2D2D] mb-1.5">
                      Who referred you? <span className="font-normal text-[#7A7A7A]">(optional)</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Name of friend or classmate"
                      value={formData.referredByName}
                      onChange={(e) => setFormData({ ...formData, referredByName: e.target.value })}
                      className="w-full px-4 py-3 border-[1.5px] border-gray-200 rounded-lg bg-white text-[#2D2D2D] focus:border-[#8BAA8B] focus:ring-2 focus:ring-[#8BAA8B]/15 outline-none transition-all"
                    />
                    <div className="bg-[#F5E6D8] rounded-lg px-4 py-3 mt-2 text-xs text-[#C17B4A] leading-relaxed">
                      <strong>🎁 Bring a Friend Bonus:</strong> You'll both receive an exclusive CRPO Prep Resource Pack after the workshop!
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-[#C17B4A] hover:bg-[#a86a3f] text-white rounded-full font-bold text-lg transition-all hover:-translate-y-0.5 shadow-[0_4px_20px_rgba(193,123,74,0.3)] disabled:opacity-60 disabled:cursor-not-allowed mt-2"
                >
                  {isSubmitting ? 'Saving...' : 'Save My Spot →'}
                </button>

                <p className="text-center text-xs text-[#7A7A7A]">
                  Your progress report will be ready before the workshop.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6">
        <div className="max-w-[1100px] mx-auto">
          <div className="text-center mb-10">
            <div className="text-xs font-bold text-[#8BAA8B] uppercase tracking-widest mb-3">Questions?</div>
            <h2 className="font-serif text-3xl md:text-4xl text-[#2D2D2D]">Frequently Asked Questions</h2>
          </div>

          <div className="max-w-[700px] mx-auto">
            {faqs.map((faq, i) => (
              <div key={i} className="border-b border-[#8BAA8B]/20">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full py-6 text-left flex justify-between items-center gap-4 text-lg font-semibold text-[#2D2D2D] hover:text-[#5E7D5E] transition-colors"
                >
                  {faq.question}
                  <span className={`w-7 h-7 min-w-7 bg-[#EDF5ED] rounded-full flex items-center justify-center text-lg text-[#8BAA8B] transition-transform ${openFaq === i ? 'rotate-45' : ''}`}>
                    +
                  </span>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFaq === i ? 'max-h-[300px] pb-6' : 'max-h-0'}`}>
                  <p className="text-sm text-[#7A7A7A] leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6 bg-gradient-to-br from-[#5E7D5E] to-[#4A6B4A] text-center">
        <h2 className="font-serif text-2xl md:text-3xl text-white mb-3">
          Don't wing the most important exam of your career.
        </h2>
        <p className="text-white/80 mb-8">
          Join 147+ interns who already have a plan. {formattedShortDate} at {formattedTime}. Counts towards your PD hours.
        </p>
        <button
          onClick={scrollToRegister}
          className="inline-flex items-center gap-2.5 bg-white text-[#5E7D5E] px-9 py-4 rounded-full font-bold text-lg transition-all hover:-translate-y-0.5 shadow-[0_4px_20px_rgba(0,0,0,0.15)]"
        >
          Register Now
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </button>
      </section>
    </div>
  )
}
